import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { EventEmitter } from '@src/common/EventEmitter';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

type Props = {
  title: string;
  subtitle?: string;
  collapseEventName: string;
  uncollapseEventName: string;
  id?: string;
  icon?: ReactElement | null | undefined;
  children: ReactElement;
};

export const CollapsibleSectionWithListener = ({ icon, ...props }: Props) => {
  const [open, setOpen] = useState(true);

  const collapse = async () => {
    setOpen(false);
  };
  const uncollapse = async () => {
    setOpen(true);
  };

  const handleClick = () => {
    setOpen((previous) => !previous);
  };

  useEffect(() => {
    EventEmitter.listen(props.collapseEventName, collapse);
    EventEmitter.listen(props.uncollapseEventName, uncollapse);

    return () => {
      EventEmitter.removeListener(props.collapseEventName, collapse);
      EventEmitter.removeListener(props.uncollapseEventName, uncollapse);
    };
  }, [props.collapseEventName, props.uncollapseEventName]);

  return (
    <Stack
      bgcolor={theme.palette.surface.surfaceContainerLowest}
      borderRadius="12px"
      height="fit-content"
      aria-labelledby={props.id}
    >
      <Stack
        style={{ cursor: 'pointer' }}
        direction="row"
        padding={theme.spacing['4']}
        justifyContent="space-between"
        alignItems="center"
        onClick={handleClick}
      >
        <Stack direction="row" gap="12px" alignItems="center">
          <Typography component="h2" variant="h5" id={props.id}>
            {props.title}
          </Typography>
          {icon}
        </Stack>
        {open ? <ExpandLess /> : <ExpandMore />}
      </Stack>
      <Collapse in={open}>
        {props.subtitle && (
          <Stack
            direction="row"
            gap="12px"
            alignItems="center"
            padding={theme.spacing['4']}
            marginTop={`-${theme.spacing['8']}`}
          >
            <Typography style={{ color: colors.text.secondary }}>
              {props.subtitle}
            </Typography>
          </Stack>
        )}
        <Stack paddingX={theme.spacing['7']} paddingY={theme.spacing['4']}>
          {props.children}
        </Stack>
      </Collapse>
    </Stack>
  );
};
